import { ITEM_STATUSES, MINIMUM_MARGIN_FLOOR_PERCENT, type ItemSupplierStatus } from '../catalog/catalog';
import { cents, formatCurrency, toCents } from '../money';
import { fill, t, type StringKey } from '../strings';
import type { ListFilterSelection } from './listSelection';

/**
 * 🔒 The bulk edit's field set (piece 13; decisions 0982, 2890). The Items list
 * edits sixteen fields an Item shares with its neighbours: the two prices,
 * the status, the category, the unit, the tax category, the two accounts, the
 * manufacturer, the type, the two margins, the returnable default, since
 * decision 7640 the image, which travels by its own route with the picture,
 * and since decision 7790 one attribute value, which travels by its own route
 * with the attribute type, the unit and the value, and since decision 8780 one
 * supplier added, which travels by its own route with the company and the
 * status. The Companies list edits the account's sales rep. 🔨 Piece 31 refuses a bulk edit
 * over an arbitrary field set, so the set is declared here once and the route
 * refuses any other field by name; a field that names one Item, such as the
 * SKU, the name or the replaced-by item, stays one at a time.
 */
export const BULK_EDIT_FIELDS = {
  Item: [
    'salesPrice', 'purchasePrice', 'status',
    'categoryId', 'unitId', 'taxCategoryId', 'salesAccountId', 'purchaseAccountId',
    'manufacturerCompanyId', 'type', 'defaultMargin', 'minimumMargin', 'returnableDefault', 'imageFileId', 'attribute', 'supplier',
  ],
  CompanyRecord: ['assignedSalesRepEmploymentId'],
} as const;

export type BulkEditRecordType = keyof typeof BULK_EDIT_FIELDS;
export type BulkEditField = (typeof BULK_EDIT_FIELDS)[BulkEditRecordType][number];

/** 🔨 A request carries at most this many rows; a list page reads 200 at a time, so only a scripted call meets it. */
export const BULK_EDIT_MAX_ROWS = 500;

export const BULK_EDIT_PATH = '/bulk-edit';

/** 🔒 The route that sets one picture on, or removes it from, every ticked Item (decision 7640): multipart, because the picture rides with the ids. */
export const BULK_EDIT_IMAGE_PATH = `${BULK_EDIT_PATH}/image`;

/**
 * 🔒 The image route's `mode` part (decision 7871, amending 7640): Set image carries exactly one
 * picture and Remove image none, so a request with no picture never clears the image by omission.
 */
export const BULK_EDIT_IMAGE_MODES = ['set', 'remove'] as const;

/** 🔨 The mode's refusal, checked once before any row: a mode named, and the picture its mode takes. */
export function bulkEditImageModeProblem(mode: unknown, hasPicture: boolean): string | null {
  if (typeof mode !== 'string' || !(BULK_EDIT_IMAGE_MODES as readonly string[]).includes(mode)) return 'an image edit names its mode, set or remove';
  if (mode === 'set' && !hasPicture) return 'Set image carries the picture to set';
  if (mode === 'remove' && hasPicture) return 'Remove image carries no picture';
  return null;
}

/** 🔒 The routes that set one attribute value on every ticked Item that carries it, and that name which ticked Items do and do not (decision 7790). */
export const BULK_EDIT_ATTRIBUTE_PATH = `${BULK_EDIT_PATH}/attribute`;
export const BULK_EDIT_ATTRIBUTE_REVIEW_PATH = `${BULK_EDIT_ATTRIBUTE_PATH}/review`;

/** 🔒 The route that adds one supplier to every ticked Item (decision 8780). */
export const BULK_EDIT_SUPPLIER_PATH = `${BULK_EDIT_PATH}/supplier`;

/** 🔒 The two statuses a bulk-added supplier takes (decision 8780): NotApplicable and Blacklisted are set Item by Item from the Sourcing row's pencil. */
export const BULK_EDIT_SUPPLIER_STATUSES = ['Primary', 'Active'] as const satisfies readonly ItemSupplierStatus[];
export type BulkEditSupplierStatus = (typeof BULK_EDIT_SUPPLIER_STATUSES)[number];

/** The string key each field's label reads (decision 0013's one lookup). */
export const BULK_EDIT_LABEL_KEYS: Readonly<Record<BulkEditField, StringKey>> = {
  salesPrice: 'bulkEdit.field.salesPrice',
  purchasePrice: 'bulkEdit.field.purchasePrice',
  status: 'bulkEdit.field.status',
  categoryId: 'bulkEdit.field.category',
  unitId: 'bulkEdit.field.unit',
  taxCategoryId: 'bulkEdit.field.taxCategory',
  salesAccountId: 'bulkEdit.field.salesAccountId',
  purchaseAccountId: 'bulkEdit.field.purchaseAccountId',
  manufacturerCompanyId: 'bulkEdit.field.manufacturer',
  type: 'bulkEdit.field.type',
  defaultMargin: 'bulkEdit.field.defaultMargin',
  minimumMargin: 'bulkEdit.field.minimumMargin',
  returnableDefault: 'bulkEdit.field.returnable',
  imageFileId: 'bulkEdit.field.image',
  attribute: 'bulkEdit.field.attribute',
  supplier: 'bulkEdit.field.supplier',
  assignedSalesRepEmploymentId: 'bulkEdit.field.salesRep',
};

/** A field whose value names another record by its id: the picker shows a name, the route takes the id. */
export type BulkEditReferenceField = 'categoryId' | 'unitId' | 'taxCategoryId' | 'salesAccountId' | 'purchaseAccountId' | 'manufacturerCompanyId' | 'assignedSalesRepEmploymentId';
const REFERENCE_FIELDS: readonly BulkEditField[] = ['categoryId', 'unitId', 'taxCategoryId', 'salesAccountId', 'purchaseAccountId', 'manufacturerCompanyId', 'assignedSalesRepEmploymentId'];

/**
 * 🔒 The account types each Item account takes (decision 2890): the sales account
 * is a Revenue account, and the purchase account is an Asset, the inventory a
 * Good is bought into, or an Expense, the cost a Service is bought as (decision
 * 0210's defaults are 1200 and 6620). The picker offers these and the route
 * refuses any other before a row is written.
 */
export const BULK_EDIT_ACCOUNT_TYPES: Readonly<Record<'salesAccountId' | 'purchaseAccountId', readonly string[]>> = {
  salesAccountId: ['Revenue'],
  purchaseAccountId: ['Asset', 'Expense'],
};

/** 🔒 The fields the Item form draws for a Good alone (piece 17, figure 107): a bulk edit refuses them on a Service row, as the form never offers them there. */
export const BULK_EDIT_GOODS_ONLY_FIELDS: readonly BulkEditField[] = ['manufacturerCompanyId', 'returnableDefault'];

/** The two Item types a bulk edit writes. */
export const BULK_EDIT_ITEM_TYPES = ['Good', 'Service'] as const;

export function isBulkEditRecordType(value: unknown): value is BulkEditRecordType {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(BULK_EDIT_FIELDS, value);
}

/** The fields a list's bulk edit offers, or none for a list that offers no bulk edit. */
export function bulkEditFieldsFor(recordType: string): readonly BulkEditField[] {
  return isBulkEditRecordType(recordType) ? BULK_EDIT_FIELDS[recordType] : [];
}

/** A price field takes dollars, as the Item's own save does. */
export function isBulkEditPriceField(field: BulkEditField): field is 'salesPrice' | 'purchasePrice' {
  return field === 'salesPrice' || field === 'purchasePrice';
}

/** A margin field takes a whole or two-place percentage, as the Item form's percent fields do. */
export function isBulkEditPercentField(field: BulkEditField): field is 'defaultMargin' | 'minimumMargin' {
  return field === 'defaultMargin' || field === 'minimumMargin';
}

export function isBulkEditReferenceField(field: BulkEditField): field is BulkEditReferenceField {
  return REFERENCE_FIELDS.includes(field);
}

/** Two decimal places at most, as the Item's price and margin columns hold. */
function twoPlaces(value: number): boolean {
  return Math.abs(value * 100 - Math.round(value * 100)) <= 1e-6;
}

/** 🔨 The rows a bulk edit names: at least one, each an id, and no more than the ceiling. */
export function bulkEditIdsProblem(ids: unknown): string | null {
  if (!Array.isArray(ids) || ids.length === 0 || !ids.every((id) => typeof id === 'string' && id.length > 0)) return 'a bulk edit names the rows it changes';
  if (ids.length > BULK_EDIT_MAX_ROWS) return `a bulk edit changes at most ${BULK_EDIT_MAX_ROWS} rows at a time; this one names ${ids.length}`;
  return null;
}

/**
 * 🔒 The rows a bulk edit changes, as its request names them (decision 7870, amending 7780): the ticked
 * ids, or over Select all N matching the list's filter selection, which the server resolves. Never both.
 */
export interface BulkEditTarget {
  ids?: unknown;
  selection?: unknown;
}

/** 🔨 A filter selection's shape: the list read's query in text and the unticked ids, or the sentence refusing it. */
export function filterSelectionProblem(raw: unknown): string | null {
  const body = (raw ?? {}) as { query?: unknown; exclusions?: unknown };
  if (typeof body.query !== 'string') return t('list.selection.shape');
  if (!Array.isArray(body.exclusions) || body.exclusions.some((id) => typeof id !== 'string')) return t('list.selection.shape');
  return null;
}

/** 🔨 The target's refusal, checked once before any row: a filter selection of the right shape, or ids within the ceiling. */
export function bulkEditTargetProblem(input: BulkEditTarget): string | null {
  if (input.selection === undefined || input.selection === null) return bulkEditIdsProblem(input.ids);
  if (input.ids !== undefined && input.ids !== null) return 'a bulk edit names its rows or the list\'s filter, never both';
  return filterSelectionProblem(input.selection);
}

/** The target's filter selection, once its shape is checked; null where the request names its ids. */
export function bulkEditSelectionOf(input: BulkEditTarget): ListFilterSelection | null {
  return input.selection === undefined || input.selection === null ? null : (input.selection as ListFilterSelection);
}

/**
 * 🔨 The whole request's refusal, checked once before any row is written: the
 * field is one this list edits, the rows are named and within the ceiling, and
 * the value is one the field takes. A row's own refusal is its single edit's.
 * A reference's existence, and an account's type, is the server's own check,
 * made once against the database before any row (decision 2890).
 */
export function bulkEditProblem(input: { recordType: unknown; field: unknown; value: unknown } & BulkEditTarget): string | null {
  if (!isBulkEditRecordType(input.recordType)) return 'a bulk edit runs on the Items list or the Companies list';
  const fields: readonly string[] = BULK_EDIT_FIELDS[input.recordType];
  if (typeof input.field !== 'string' || !fields.includes(input.field)) return `a bulk edit on this list changes ${fields.join(', ')} and no other field`;
  const idsProblem = bulkEditTargetProblem(input);
  if (idsProblem) return idsProblem;
  const field = input.field as BulkEditField;
  if (field === 'imageFileId') return 'an image is set or removed through the image route, which carries the picture';
  if (field === 'attribute') return 'an attribute is set through the attribute route, which carries the type, the unit and the value';
  if (field === 'supplier') return 'a supplier is added through the supplier route, which carries the company and its status';
  if (isBulkEditPriceField(field)) {
    if (typeof input.value !== 'number' || !Number.isFinite(input.value)) return 'a price is a number of dollars';
    if (input.value < 0) return 'a price is zero or more';
    if (!twoPlaces(input.value)) return 'a price carries at most two decimal places';
    return null;
  }
  if (isBulkEditPercentField(field)) {
    if (typeof input.value !== 'number' || !Number.isFinite(input.value)) return 'a margin is a number of percent';
    if (input.value < 0 || input.value >= 100) return 'a margin is zero or more and under 100 percent';
    if (!twoPlaces(input.value)) return 'a margin carries at most two decimal places';
    // 🔒 A minimum margin is one percent or more, so every sale makes money (decision 3140).
    if (field === 'minimumMargin' && input.value < MINIMUM_MARGIN_FLOOR_PERCENT) return `a minimum margin is ${MINIMUM_MARGIN_FLOOR_PERCENT} percent or more`;
    return null;
  }
  if (field === 'status') {
    if (typeof input.value !== 'string' || !(ITEM_STATUSES as readonly string[]).includes(input.value)) return `a status is one of ${ITEM_STATUSES.join(', ')}`;
    return null;
  }
  if (field === 'type') {
    if (typeof input.value !== 'string' || !(BULK_EDIT_ITEM_TYPES as readonly string[]).includes(input.value)) return `a type is one of ${BULK_EDIT_ITEM_TYPES.join(', ')}`;
    return null;
  }
  if (field === 'returnableDefault') {
    if (typeof input.value !== 'boolean') return 'returnable is yes or no';
    return null;
  }
  if (typeof input.value !== 'string' || input.value.trim() === '') {
    return field === 'assignedSalesRepEmploymentId' ? 'a sales rep names the employment that takes the accounts' : `a bulk edit of the ${t(BULK_EDIT_LABEL_KEYS[field]).toLowerCase()} names the record it points at`;
  }
  return null;
}

/** What `POST /bulk-edit/attribute` takes besides the ticked rows: the catalog attribute type and its value in the type's kind (decision 7790). */
export interface BulkEditAttributeInput {
  attributeTypeId?: unknown;
  unitId?: unknown;
  enteredValue?: unknown;
  attributeTypeValueId?: unknown;
}

/** 🔨 The attribute request's shape, checked once before any row: the rows are named, the type is named, and a value is a number with a unit or a chosen value, never both. */
export function bulkEditAttributeProblem(input: BulkEditAttributeInput & BulkEditTarget, review = false): string | null {
  const idsProblem = bulkEditTargetProblem(input);
  if (idsProblem) return idsProblem;
  if (typeof input.attributeTypeId !== 'string' || input.attributeTypeId.trim() === '') return 'a bulk edit of an attribute names the attribute type';
  if (review) return null;
  const hasNumber = input.enteredValue !== null && input.enteredValue !== undefined;
  const hasValue = typeof input.attributeTypeValueId === 'string' && input.attributeTypeValueId !== '';
  if (hasNumber && hasValue) return 'an attribute takes a number with its unit, or one of its values, not both';
  if (hasNumber && (typeof input.enteredValue !== 'number' || !Number.isFinite(input.enteredValue))) return 'a measured attribute is a number';
  if (!hasNumber && !hasValue) return 'a bulk edit of an attribute names the value to set';
  return null;
}

/** What `POST /bulk-edit/supplier` takes besides the ticked rows: the company and the status it is added at (decision 8780). */
export interface BulkEditSupplierInput {
  companyId?: unknown;
  status?: unknown;
}

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** 🔨 The supplier request's shape, checked once before any row: the rows are named, the company is a record id, and the status is Primary or Active. */
export function bulkEditSupplierProblem(input: BulkEditSupplierInput & BulkEditTarget): string | null {
  const idsProblem = bulkEditTargetProblem(input);
  if (idsProblem) return idsProblem;
  if (typeof input.companyId !== 'string' || input.companyId.trim() === '') return t('bulkEdit.supplier.namesCompany');
  if (!UUID_SHAPE.test(input.companyId)) return t('bulkEdit.supplier.notCompany');
  if (typeof input.status !== 'string' || !(BULK_EDIT_SUPPLIER_STATUSES as readonly string[]).includes(input.status)) return fill(t('bulkEdit.supplier.statusProblem'), { statuses: BULK_EDIT_SUPPLIER_STATUSES.join(' or ') });
  return null;
}

/** The string key each supplier status reads in, as the Sourcing tile's status control labels it. */
const SUPPLIER_STATUS_KEYS: Readonly<Record<ItemSupplierStatus, StringKey>> = {
  Primary: 'item.supplier.status.primary',
  Active: 'item.supplier.status.active',
  NotApplicable: 'item.supplier.status.notApplicable',
  Blacklisted: 'item.supplier.status.blacklisted',
};

/** 🔒 A supplier row as a bulk edit's History and confirmation read it (decision 8780): the company, then its status in brackets. */
export function bulkEditSupplierText(companyName: string, status: ItemSupplierStatus): string {
  return `${companyName} (${t(SUPPLIER_STATUS_KEYS[status])})`;
}

/** One row a bulk edit refused, named, with the refusal its single edit gave. */
export interface BulkEditRefusal {
  id: string;
  label: string;
  reason: string;
}

/** What a bulk edit answers: how many rows it wrote, each it refused, and each written row whose History entry failed (decision 1604). */
export interface BulkEditAnswer {
  accepted: number;
  refused: BulkEditRefusal[];
  /** A row the edit wrote whose audit event was not written; the value stands, and the reason says so. */
  historyMissed?: BulkEditRefusal[];
}

/**
 * 🔒 One field a bulk edit changed on one record, as its audit row keeps it
 * (decisions 1604, 2890): the field, the value before and after, and for a
 * field that points at another record the names the two read as when the edit
 * ran.
 */
export interface BulkEditChange {
  field: BulkEditField;
  from: number | string | boolean | null;
  to: number | string | boolean | null;
  fromName?: string | null;
  toName?: string | null;
  /** The attribute type's name, on an `attribute` change (decision 7790): the History line reads it where other fields read their label. */
  attributeName?: string;
}

/** The ticked rows an attribute review reads, split by whether the row's category carries the attribute (decision 7790). */
export interface BulkEditAttributeReview {
  attributeName: string;
  carrying: { id: string; label: string }[];
  lacking: { id: string; label: string }[];
}

/** A value as the History row reads it: a price as money, a margin as a percent, a reference by name, a yes or no, a status or type as itself. */
function bulkEditValueText(field: BulkEditField, value: unknown, name: unknown): string {
  if (isBulkEditPriceField(field)) return typeof value === 'number' || typeof value === 'string' ? formatCurrency(cents(toCents(value))) : '—';
  if (isBulkEditPercentField(field)) return typeof value === 'number' ? `${value}%` : '—';
  if (field === 'assignedSalesRepEmploymentId') return typeof name === 'string' && name !== '' ? name : t('history.audit.bulkEditedNobody');
  if (field === 'attribute') return typeof value === 'string' && value !== '' ? value : t('history.audit.bulkEditedNoAttribute');
  if (field === 'supplier') return typeof value === 'string' && value !== '' ? value : t('history.audit.bulkEditedNoSupplier');
  if (field === 'imageFileId') return typeof name === 'string' && name !== '' ? name : t('history.audit.bulkEditedNoImage');
  if (isBulkEditReferenceField(field)) return typeof name === 'string' && name !== '' ? name : '—';
  if (typeof value === 'boolean') return value ? t('record.yes') : t('record.no');
  return typeof value === 'string' ? value : '—';
}

/** 🔒 The History detail of a bulk edit's audit row (decision 1604): each field with its value before and after. */
export function bulkEditHistoryDetail(changedFields: unknown): string | null {
  if (!Array.isArray(changedFields)) return null;
  const parts = (changedFields as { field?: unknown; from?: unknown; to?: unknown; fromName?: unknown; toName?: unknown; attributeName?: unknown }[])
    .filter((change): change is BulkEditChange => typeof change.field === 'string' && Object.prototype.hasOwnProperty.call(BULK_EDIT_LABEL_KEYS, change.field))
    .map((change) => `${change.field === 'attribute' && typeof change.attributeName === 'string' && change.attributeName !== '' ? change.attributeName : t(BULK_EDIT_LABEL_KEYS[change.field])}: ${bulkEditValueText(change.field, change.from, change.fromName)} → ${bulkEditValueText(change.field, change.to, change.toName)}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}
